import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { record } from 'rrweb';
import { LoggingService } from '../logging-service/logging.service';
import { SERVER_BASE_URL } from 'src/environments/environment';
import { compress, decompress } from 'lz-string';
import { eventWithTime } from '@rrweb/types';
import { deflate } from 'pako';
import type {} from 'css-font-loading-module';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { QueryParamsService } from '../query-params/query-params.service';
import { StateService } from '../state/state.service';

@Injectable({ providedIn: 'root' })
export class RecordingService {
  private recordingByContextId: Map<string, Recording> = new Map();
  private stopRecordingFn: (() => void) | null = null;
  private saveInterval: any;
  private readonly RECORDING_KEY = 'recording-event';
  private readonly RECORDING_ERROR_KEY = 'recording-error';
  private isSaving: boolean = false;
  // The interval we convert the recording to video.
  private readonly eventConversionIntervalInMilliseconds = 15 * 60 * 1000;
  private readonly eventSaveIntervalInMilliseconds = 10 * 1000;
  private isRecordingEnabled: boolean = false;
  private readonly ERROR_THRESHOLD = 3;
  private isLoanCalculator = false;

  constructor(
    private http: HttpClient,
    private loggingService: LoggingService,
    private queryParamsService: QueryParamsService,
    protected stateService: StateService
  ) {}

  public loadRecordingService(): void {
    const token = this.queryParamsService.getQueryParams().id;
    const savedEvents = localStorage.getItem(this.RECORDING_KEY);
    if (savedEvents) {
      try {
        const savedCompressedEvents = JSON.parse(savedEvents) as {
          context: string;
          compressedRecording: string;
        }[];
        this.recordingByContextId = new Map(
          savedCompressedEvents.map((contextByCompressedEvents) => [
            contextByCompressedEvents.context,
            JSON.parse(
              decompress(contextByCompressedEvents.compressedRecording)
            ) as Recording,
          ])
        );
      } catch (error) {
        this.loggingService.error(
          'Failed to load events from localStorage',
          error
        );
        this.recordingByContextId = new Map<string, Recording>([
          [this.loggingService.getContextID(), { token: token, events: [] }],
        ]);
      }
    }

    if (!this.recordingByContextId.has(this.loggingService.getContextID())) {
      this.recordingByContextId.set(this.loggingService.getContextID(), {
        token: token,
        events: [],
      });
    }
  }

  public startRecording(): void {
    if (this.stopRecordingFn || this.isLoanCalculator) {
      return;
    }

    // Set localstorage that keeps track of failed request. In case of too many failed request,
    // we will stop recording and clear recording local storage to prevent it slowing down the page.
    if (!localStorage.getItem(this.RECORDING_ERROR_KEY)) {
      localStorage.setItem(this.RECORDING_ERROR_KEY, '0');
    }

    this.stopRecordingFn = record({
      emit: (event) => {
        this.saveEvents(event);
      },
      // We take a full screenshot if the session has gone longer than video convert interval.
      checkoutEveryNms: this.eventConversionIntervalInMilliseconds,
    });

    this.saveInterval = setInterval(() => {
      if (!this.isSaving) {
        this.saveRecordingEvents();
      }
    }, this.eventSaveIntervalInMilliseconds);
  }

  public saveSuccessRecording(): void {
    record.takeFullSnapshot(true);
    this.saveRecordingEvents();
  }

  public stopRecording(): void {
    if (!this.stopRecordingFn) {
      this.loggingService.warn('No recording in progress to stop.');
      return;
    }

    this.stopRecordingFn();
    this.stopRecordingFn = null;

    if (this.saveInterval) {
      clearInterval(this.saveInterval);
      this.saveInterval = null;
    }
  }

  private saveEvents(event: eventWithTime): void {
    const eventsForContext = this.recordingByContextId.get(
      this.loggingService.getContextID()
    );

    if (eventsForContext) {
      eventsForContext.events.push(event);
    } else {
      const token = this.queryParamsService.getQueryParams().id;
      this.recordingByContextId.set(this.loggingService.getContextID(), {
        token: token,
        events: [event],
      });
    }

    const compressedEventsByContext: {
      context: string;
      compressedRecording: string;
    }[] = [];

    this.recordingByContextId.forEach((recording, context) => {
      compressedEventsByContext.push({
        context,
        compressedRecording: compress(JSON.stringify(recording)),
      });
    });

    localStorage.setItem(
      this.RECORDING_KEY,
      JSON.stringify(compressedEventsByContext)
    );
  }

  public saveRecordingEvents(): void {
    if (
      this.recordingByContextId.size === 0 ||
      this.isRecordingEnabled === false
    ) {
      return;
    }

    this.isSaving = true;

    const eventsToSave = this.recordingByContextId;
    this.recordingByContextId = new Map<string, Recording>();

    this.http
      .post(`${SERVER_BASE_URL}/record`, {
        checkoutUserRecordings: this.toRequest(eventsToSave),
      })
      .subscribe(
        () => {
          this.isSaving = false;
          localStorage.removeItem(this.RECORDING_KEY);
          localStorage.setItem(this.RECORDING_ERROR_KEY, '0');
        },
        (err) => {
          this.loggingService.error('Failed to send recorded event', err);
          this.isSaving = false;
          // If new events have occurred during saving, concatenate those to the old ones.
          for (const [frontendContext, oldRecording] of eventsToSave) {
            const newRecording = this.recordingByContextId.get(frontendContext);
            if (newRecording) {
              let recording: Recording = {
                token: oldRecording.token,
                events: [...oldRecording.events, ...newRecording.events],
              };
              this.recordingByContextId.set(frontendContext, recording);
            } else {
              this.recordingByContextId.set(frontendContext, oldRecording);
            }
          }
          this.checkErrorThreshold();
        }
      );
  }

  private toRequest(eventsByContext: Map<string, Recording>): EventRequest[] {
    let eventRequest: EventRequest[] = [];

    for (const [contextId, recording] of eventsByContext) {
      eventRequest.push({
        frontendContextId: contextId,
        token: recording.token,
        events: recording.events.map((event) => this.compressToBase64(event)),
      });
    }

    return eventRequest;
  }

  public getRecordingStatus(): Observable<boolean> {
    return this.http.get<boolean>(`${SERVER_BASE_URL}/record/enabled`).pipe(
      tap((response) => (this.isRecordingEnabled = response)),
      catchError((err) => {
        this.loggingService.error('Failed to get recording status', err);
        return of(false);
      })
    );
  }

  public setIsLoanCalculator(isLoanCalculator: boolean): void {
    this.isLoanCalculator = isLoanCalculator;
  }

  private checkErrorThreshold(): void {
    let errorCount = Number(
      localStorage.getItem(this.RECORDING_ERROR_KEY) ?? 0
    );

    errorCount += 1;

    localStorage.setItem(this.RECORDING_ERROR_KEY, errorCount.toString());

    if (errorCount >= this.ERROR_THRESHOLD) {
      // Stop recording and remove related local data.
      this.stopRecording();
      this.recordingByContextId = new Map<string, Recording>();
      localStorage.removeItem(this.RECORDING_KEY);
      localStorage.removeItem(this.RECORDING_ERROR_KEY);
    }
  }

  private compressToBase64(obj: object): string {
    // Compressing data (approximately 1/4 of the normal size)
    const compressed = deflate(JSON.stringify(obj));
    return btoa(String.fromCharCode(...compressed));
  }
}

interface Recording {
  token: string;
  events: eventWithTime[];
}

interface EventRequest {
  frontendContextId: string;
  token: string;
  events: string[];
}
